import { isTauri } from '@/tauri/utils';
import { logger } from '@/utils/logger/logger';
import { extractPluginError, jsonSafeClone } from '@heidi/plugin-core';
import { type EventCallback, listen } from '@tauri-apps/api/event';
import type {
  AccessibilityActionResult,
  AxBatchResult,
  AxBatchStep,
  BatchResult,
  ComputerUseReadiness,
  CreateSessionOptions,
  CursorPositionResult,
  DesktopControlClientAction,
  DesktopControlDispatchResult,
  DesktopControlSnapshot,
  DesktopTargetProjectTurnProof,
  DesktopTargetSignProjectTurnParams,
  DesktopTargetStartParams,
  EnsureFileExecutorResult,
  EnsureRecordingExecutorResult,
  EnsureRunExecutorResult,
  ExecutorUploadContext,
  FileExecutorStateSnapshot,
  InspectUiRequest,
  InspectUiResult,
  NavigateEdgeRequest,
  ObserveTick,
  PerformActionRequest,
  RecordingExecutorStateSnapshot,
  Rect,
  RpaBatchStepPayload,
  RpaClientCapabilities,
  RpaExecutorPresentation,
  RpaExecutorStateSnapshot,
  ScreenshotResult,
  SetValueRequest,
  ToolAction,
  WindowLivenessResult,
} from './types';
import { notifyRpaWindowNotFound, readEhrPinGeneration } from './window-not-found';

export {
  isComputerUseUnsupported,
  parseComputerUseReadiness,
  readComputerUseRefusal,
  RPA_COMPUTER_USE_NOT_READY_ERROR_CODE,
} from './computer-use-readiness';
export {
  isWindowNotFoundError,
  reportEhrPinGeneration,
  subscribeRpaWindowNotFound,
  windowHandleFromWindowNotFound,
} from './window-not-found';

export type {
  AcquireDesktopControlResult,
  CreateSessionOptions,
  DesktopControlClientAction,
  DesktopControlDispatchResult,
  DesktopControlLease,
  DesktopControlOwner,
  DesktopControlOwnerKind,
  DesktopControlOwnerPatch,
  DesktopControlSnapshot,
  EnsureFileExecutorResult,
  EnsureRecordingExecutorResult,
  ExecutorUploadContext,
  FileExecutorStateSnapshot,
  NavigateEdgeRequest,
  ObserveCause,
  ObserveTick,
  RecordingExecutorStateSnapshot,
  RefreshDesktopControlResult,
  ReleaseDesktopControlResult,
  RpaBatchStepPayload,
  RpaBatchStepPhase,
  RpaExecutorPresentation,
  RpaExecutorStopCause,
} from './types';

const COMMAND_PREFIX = 'plugin:rpa|';
export const RPA_CANCELLED_ERROR_CODE = 'RPA_CANCELLED';
export const RPA_NAV_EDGE_REMATCH_FAILED_ERROR_CODE = 'RPA_NAV_EDGE_REMATCH_FAILED';
export const RPA_SELECTOR_AMBIGUOUS_ERROR_CODE = 'RPA_SELECTOR_AMBIGUOUS';
export const RPA_INVALID_ARGUMENT_ERROR_CODE = 'RPA_INVALID_ARGUMENT';
export const RPA_DESKTOP_CONTROL_REQUIRED_ERROR_CODE = 'RPA_DESKTOP_CONTROL_REQUIRED';
// Mirrors the Rust const `RPA_DESKTOP_CONTROL_BLOCKED` (executor/mod.rs).
export const RPA_DESKTOP_CONTROL_BLOCKED_ERROR_CODE = 'RPA_DESKTOP_CONTROL_BLOCKED';
// Tauri event name carrying Rust-owned executor snapshots from the Rust loop.
export const EXECUTOR_STATUS_EVENT = 'rpa:executor-status';
// Dedicated recording executor status event — separate slot, no desktop-control
// ownership, so it never intermingles with the computer-use executor's lifecycle.
export const RECORDING_EXECUTOR_STATUS_EVENT = 'rpa:recording-executor-status';
// Dedicated headless file executor status event — separate slot; a file run
// must never light the computer-use status pill. Same-device mode attaches the
// slot through `ensureFileExecutor` after a `file_tools` child is discovered on
// the run stream; target mode attaches it through native desktop-target admission.
export const FILE_EXECUTOR_STATUS_EVENT = 'rpa:file-executor-status';
// Per-step progress the AX batch loop emits to the copilot overlay window.
export const BATCH_STEP_EVENT = 'rpa:batch-step';
// Observe-watcher snapshots/errors, emitted to the window that called
// `observe_start` (mirrors the Rust const in accessibility/watcher.rs).
export const OBSERVE_TICK_EVENT = 'rpa:observe-tick';
export const DESKTOP_TARGET_IDENTITY_CHANGED_EVENT =
  'rpa:desktop-target-identity-changed';

export interface OverlayPrepareResult {
  readonly contentReady: boolean;
}

export class RpaBridgeError extends Error {
  readonly errorCode?: string;
  readonly details?: unknown;
  constructor(message: string, errorCode?: string, details?: unknown) {
    super(message);
    this.name = 'RpaBridgeError';
    this.errorCode = errorCode;
    this.details = details;
  }
}

export const hasRpaErrorCode = (error: unknown, errorCode: string): boolean =>
  error instanceof Error &&
  'errorCode' in error &&
  (error as { errorCode?: unknown }).errorCode === errorCode;

export async function listenRpaExecutorStatus(
  cb: EventCallback<RpaExecutorStateSnapshot>
): Promise<() => void> {
  if (!isTauri()) return () => {};
  return listen<RpaExecutorStateSnapshot>(EXECUTOR_STATUS_EVENT, cb);
}

export async function listenRecordingExecutorStatus(
  cb: EventCallback<RecordingExecutorStateSnapshot>
): Promise<() => void> {
  if (!isTauri()) return () => {};
  return listen<RecordingExecutorStateSnapshot>(RECORDING_EXECUTOR_STATUS_EVENT, cb);
}

export async function listenFileExecutorStatus(
  cb: EventCallback<FileExecutorStateSnapshot>
): Promise<() => void> {
  if (!isTauri()) return () => {};
  return listen<FileExecutorStateSnapshot>(FILE_EXECUTOR_STATUS_EVENT, cb);
}

export async function listenRpaBatchStep(
  cb: EventCallback<RpaBatchStepPayload>
): Promise<() => void> {
  if (!isTauri()) return () => {};
  return listen<RpaBatchStepPayload>(BATCH_STEP_EVENT, cb);
}

export async function listenObserveTick(
  cb: EventCallback<ObserveTick>
): Promise<() => void> {
  if (!isTauri()) return () => {};
  return listen<ObserveTick>(OBSERVE_TICK_EVENT, cb);
}

export async function listenDesktopTargetIdentityChanged(
  cb: EventCallback<void>
): Promise<() => void> {
  if (!isTauri()) return () => {};
  return listen<void>(DESKTOP_TARGET_IDENTITY_CHANGED_EVENT, cb);
}

async function invokeRpaCommand<T>(
  command: string,
  payload?: Record<string, unknown>
): Promise<T> {
  if (!isTauri()) {
    throw new RpaBridgeError('RPA bridge is only available inside the Tauri runtime');
  }

  const { invoke } = await import('@tauri-apps/api/core');
  const generationAtStart = readEhrPinGeneration();
  try {
    return await invoke<T>(`${COMMAND_PREFIX}${command}`, payload ?? {});
  } catch (error) {
    const { message, errorCode, details } = extractPluginError(error);
    const safeDetails = jsonSafeClone(details);

    logger.debug('RPA command error', {
      command,
      message,
      errorCode,
      details: safeDetails,
    });

    const bridgeError = new RpaBridgeError(message, errorCode, safeDetails);
    notifyRpaWindowNotFound(bridgeError, payload, generationAtStart);
    throw bridgeError;
  }
}

const dispatchDesktopControl = (
  action: DesktopControlClientAction
): Promise<DesktopControlDispatchResult> =>
  invokeRpaCommand<DesktopControlDispatchResult>('dispatch_desktop_control', {
    action,
  });

export const rpaBridge = {
  isAvailable(): boolean {
    return isTauri();
  },

  // -- Session --

  async createSession(sessionId: string, options?: CreateSessionOptions): Promise<void> {
    return invokeRpaCommand<void>(
      'create_session',
      options ? { sessionId, options } : { sessionId }
    );
  },

  async destroySession(sessionId: string): Promise<void> {
    return invokeRpaCommand<void>('destroy_session', { sessionId });
  },

  async cancelSession(sessionId: string): Promise<void> {
    return invokeRpaCommand<void>('cancel_session', { sessionId });
  },

  /**
   * Bind (or clear, with []) the display names sessionless inspects (the
   * toolbar observe loop) must redact. Session inspects never read this bind.
   */
  async setAmbientRedactions(values: string[]): Promise<void> {
    return invokeRpaCommand<void>('set_ambient_redactions', { values });
  },

  // -- Desktop control --

  async dispatchDesktopControl(
    action: DesktopControlClientAction
  ): Promise<DesktopControlDispatchResult> {
    return dispatchDesktopControl(action);
  },

  async readDesktopControlSnapshot(): Promise<DesktopControlSnapshot> {
    return invokeRpaCommand<DesktopControlSnapshot>('read_desktop_control_snapshot');
  },

  async setCuaWakeAssertionEnabled(enabled: boolean, baseUrl: string): Promise<void> {
    return invokeRpaCommand<void>('set_cua_wake_assertion_enabled', { enabled, baseUrl });
  },

  async setComputerUseReadinessGateEnforced(enforced: boolean): Promise<void> {
    return invokeRpaCommand<void>('set_computer_use_readiness_gate_enforced', {
      enforced,
    });
  },

  async readComputerUseReadiness(): Promise<ComputerUseReadiness> {
    return invokeRpaCommand<ComputerUseReadiness>('read_computer_use_readiness');
  },

  // -- Overlay --
  async overlayPrepare(): Promise<OverlayPrepareResult> {
    return invokeRpaCommand<OverlayPrepareResult>('overlay_prepare');
  },

  async overlayContentReady(): Promise<void> {
    return invokeRpaCommand<void>('overlay_content_ready');
  },

  async overlayHide(): Promise<void> {
    return invokeRpaCommand<void>('overlay_hide');
  },

  async overlaySetEnabled(enabled: boolean): Promise<void> {
    return invokeRpaCommand<void>('overlay_set_enabled', { enabled });
  },

  /**
   * Register/unregister the OS-level CmdOrCtrl+Shift+H summon hotkey.
   * Independent of {@link overlaySetEnabled} so agent paths can enable the
   * overlay window without defeating the kill switch.
   */
  async overlaySetHotkeyEnabled(enabled: boolean): Promise<void> {
    return invokeRpaCommand<void>('overlay_set_hotkey_enabled', { enabled });
  },

  /**
   * Suppress the overlay's blur-driven auto-hide while a native dialog
   * (e.g. the file picker) is open — the dialog steals key-window focus
   * and would otherwise dismiss the overlay. Call with ``true`` right
   * before opening the dialog and ``false`` once focus returns.
   */
  async overlaySetSuppressBlur(suppressed: boolean): Promise<void> {
    return invokeRpaCommand<void>('overlay_set_suppress_blur', { suppressed });
  },

  /**
   * Resize the overlay window to ``contentHeightPx`` logical pixels for
   * an idle React surface (assistant, inspector, naming, …). Width is
   * canonical and owned by the Rust controller; this command only
   * supplies the React-computed intrinsic content height.
   */
  async overlaySetHeight(contentHeightPx: number): Promise<void> {
    return invokeRpaCommand<void>('overlay_set_height', { contentHeightPx });
  },

  /**
   * Resize the cua-status window and show it on the first push.
   * Width / bottom-dock geometry are owned by Rust.
   * Returns false when the panel has not accepted the measurement yet.
   */
  async cuaStatusSetHeight(contentHeightPx: number): Promise<boolean> {
    return invokeRpaCommand<boolean>('cua_status_set_height', { contentHeightPx });
  },

  async showElementHighlight(bounds: Rect, windowBounds?: Rect): Promise<void> {
    return invokeRpaCommand<void>('show_element_highlight', { bounds, windowBounds });
  },

  async clearElementHighlight(): Promise<void> {
    return invokeRpaCommand<void>('clear_element_highlight');
  },

  // -- Layer 1: Raw Input --

  async click(
    sessionId: string,
    x: number,
    y: number,
    options?: { button?: string; count?: number; modifier?: string }
  ): Promise<void> {
    return invokeRpaCommand<void>('click', {
      sessionId,
      x,
      y,
      ...options,
    });
  },

  async moveMouse(sessionId: string, x: number, y: number): Promise<void> {
    return invokeRpaCommand<void>('move_mouse', { sessionId, x, y });
  },

  async drag(
    sessionId: string,
    startX: number,
    startY: number,
    endX: number,
    endY: number,
    options?: { button?: string }
  ): Promise<void> {
    return invokeRpaCommand<void>('drag', {
      sessionId,
      startX,
      startY,
      endX,
      endY,
      ...options,
    });
  },

  async typeText(sessionId: string, text: string): Promise<void> {
    return invokeRpaCommand<void>('type_text', { sessionId, text });
  },

  async pressKey(sessionId: string, chord: string): Promise<void> {
    return invokeRpaCommand<void>('press_key', { sessionId, chord });
  },

  async holdKey(sessionId: string, key: string, durationMs: number): Promise<void> {
    return invokeRpaCommand<void>('hold_key', {
      sessionId,
      key,
      durationMs,
    });
  },

  async scroll(
    sessionId: string,
    direction: string,
    amount: number,
    options?: { modifier?: string; x?: number; y?: number }
  ): Promise<void> {
    return invokeRpaCommand<void>('scroll', {
      sessionId,
      direction,
      amount,
      ...options,
    });
  },

  async mouseButton(
    sessionId: string,
    button: string,
    buttonState: string
  ): Promise<void> {
    return invokeRpaCommand<void>('mouse_button', {
      sessionId,
      button,
      buttonState,
    });
  },

  // -- Screenshot --

  async screenshot(sessionId: string): Promise<ScreenshotResult> {
    return invokeRpaCommand<ScreenshotResult>('screenshot', { sessionId });
  },

  async zoom(
    sessionId: string,
    x0: number,
    y0: number,
    x1: number,
    y1: number
  ): Promise<ScreenshotResult> {
    return invokeRpaCommand<ScreenshotResult>('zoom', {
      sessionId,
      x0,
      y0,
      x1,
      y1,
    });
  },

  async cursorPosition(sessionId: string): Promise<CursorPositionResult> {
    return invokeRpaCommand<CursorPositionResult>('cursor_position', {
      sessionId,
    });
  },

  async getCapabilities(): Promise<RpaClientCapabilities> {
    return invokeRpaCommand<RpaClientCapabilities>('get_capabilities');
  },

  // -- Layer 2: Batch & Window management --

  async executeBatch(sessionId: string, actions: ToolAction[]): Promise<BatchResult> {
    return invokeRpaCommand<BatchResult>('execute_batch', {
      sessionId,
      actions,
    });
  },

  // -- Accessibility Automation: snapshot/action surface --

  async inspectUi(
    sessionId: string,
    request?: Partial<InspectUiRequest>
  ): Promise<InspectUiResult> {
    return invokeRpaCommand<InspectUiResult>('inspect_ui', { sessionId, request });
  },

  async inspectUiReadOnly(request?: Partial<InspectUiRequest>): Promise<InspectUiResult> {
    return invokeRpaCommand<InspectUiResult>('inspect_ui', { request });
  },

  /**
   * See `WindowLivenessResult`. `includeFrontmost: false` reports
   * `frontmost: false` and skips that lookup. Omitted defaults to true.
   */
  async windowLiveness(
    sessionId: string,
    windowHandle: number,
    options?: { includeFrontmost?: boolean }
  ): Promise<WindowLivenessResult> {
    return invokeRpaCommand<WindowLivenessResult>('window_liveness', {
      sessionId,
      windowHandle,
      ...(options?.includeFrontmost === undefined
        ? {}
        : { includeFrontmost: options.includeFrontmost }),
    });
  },

  // -- Sessionless observe watcher (copilot overlay) --

  /**
   * Start (or restart) the Rust observe watcher. Snapshots and typed errors
   * arrive on `rpa:observe-tick`, targeted at the calling window — install the
   * listener first (see `listenObserveTick`), then start.
   */
  async observeStart(request?: Partial<InspectUiRequest>): Promise<void> {
    return invokeRpaCommand<void>('observe_start', { request });
  },

  /** Stop the observe watcher. Resolves whether one was running. */
  async observeStop(): Promise<boolean> {
    return invokeRpaCommand<boolean>('observe_stop');
  },

  /**
   * Ask the running watcher for an immediate capture (the overlay's refresh).
   * Resolves `false` when no watcher is running.
   */
  async observeCaptureNow(): Promise<boolean> {
    return invokeRpaCommand<boolean>('observe_capture_now');
  },

  async navigateEdge(
    sessionId: string,
    request: NavigateEdgeRequest
  ): Promise<InspectUiResult> {
    return invokeRpaCommand<InspectUiResult>('navigate_edge', {
      sessionId,
      request,
    });
  },

  async performAction(
    sessionId: string,
    request: PerformActionRequest
  ): Promise<AccessibilityActionResult> {
    return invokeRpaCommand<AccessibilityActionResult>('perform_action', {
      sessionId,
      request,
    });
  },

  async setValue(
    sessionId: string,
    request: SetValueRequest
  ): Promise<AccessibilityActionResult> {
    return invokeRpaCommand<AccessibilityActionResult>('set_value', {
      sessionId,
      request,
    });
  },

  /**
   * Run a sequence of selector-addressed AX steps in one call. The IPC response
   * never carries the batch's trailing screenshot — the desktop capture stays
   * in-process (executor-only). The copilot warm-apply is the overlay caller and
   * builds only `set_value` / `wait_for` steps (never `perform_action`); that
   * contract is test-locked in the apply module.
   */
  async performActionBatch(
    sessionId: string,
    steps: AxBatchStep[]
  ): Promise<AxBatchResult> {
    return invokeRpaCommand<AxBatchResult>('perform_action_batch', {
      sessionId,
      steps,
    });
  },

  // -- Agent-harness executor --

  /**
   * Idempotently ensure the Rust executor owns `agentRunId`. It opens the
   * per-run action stream, executes remote tool requests against the GUI, and
   * POSTs results — no JS in the execution loop. If another run is already
   * active, returns `outcome: "busy"` with Rust's actual active run rather than
   * asking JS to infer ownership from an error.
   *
   * The executor owns a mutating RPA session and acquires/refreshes its own
   * desktop-control lease natively. Native command handlers revalidate that
   * lease before each desktop action.
   *
   * Capability note: the executor commands are granted to the **main window**
   * only (`rpa:executor` in `capabilities/rpa.json`). Calling them from the
   * overlay webview would be rejected unless `overlay.json` is granted that
   * narrower permission set.
   */
  async ensureRunExecutor(params: {
    agentRunId: string;
    baseUrl: string;
    bearerToken: string;
    /** Stored on the slot; see `ExecutorPresentation` (dormant until AGE-929/930). */
    presentation?: RpaExecutorPresentation;
    /**
     * Display names the executor session must redact from accessible labels,
     * bound atomically with session creation (before the tool loop starts).
     */
    redactionValues?: string[];
  }): Promise<EnsureRunExecutorResult> {
    return invokeRpaCommand<EnsureRunExecutorResult>('ensure_run_executor', params);
  },

  /**
   * Reuse the live executor for `agentRunId`, refreshing its bearer token, or
   * `null` when no live matching executor exists. Never starts one — the reuse
   * arm of `ensureRunExecutor` alone. `outcome: "busy"` names another run
   * holding the slot, exactly as `ensureRunExecutor` reports it.
   */
  async attachExistingRunExecutor(params: {
    agentRunId: string;
    bearerToken: string;
  }): Promise<EnsureRunExecutorResult | null> {
    return invokeRpaCommand<EnsureRunExecutorResult | null>(
      'attach_existing_run_executor',
      params
    );
  },

  /**
   * Hydrate JS from Rust's current executor slot after a webview mount/reload.
   */
  async readRunExecutorState(): Promise<RpaExecutorStateSnapshot | null> {
    return invokeRpaCommand<RpaExecutorStateSnapshot | null>('read_run_executor_state');
  },

  /**
   * Signal the running executor to stop. Returns as soon as the signal is
   * accepted; the run drains its in-flight tool and reports completion via the
   * `Stopped` `rpa:executor-status` event. Wait for that `Stopped` event before
   * starting a new executor — an immediate restart can otherwise be rejected
   * with `RPA_EXECUTOR_ALREADY_RUNNING` while the previous run is still
   * draining a non-cancellable tool.
   */
  async stopRunExecutor(): Promise<void> {
    return invokeRpaCommand<void>('stop_run_executor');
  },

  /** Stop only if `agentRunId` still owns the executor slot. */
  async stopRunExecutorFor(agentRunId: string): Promise<boolean> {
    return invokeRpaCommand<boolean>('stop_run_executor_for', { agentRunId });
  },

  /**
   * Push a refreshed bearer token to the running executor (used when it parks
   * on a 401). No-op if none is running.
   */
  async updateRunExecutorToken(bearerToken: string): Promise<void> {
    return invokeRpaCommand<void>('update_run_executor_token', { bearerToken });
  },

  // -- Recording executor --
  //
  // A dedicated executor for recording child runs. Unlike the computer-use
  // executor it owns NO desktop-control lease, so there is no presentation,
  // no redaction binding, and no approval reservation release. It only opens
  // the per-run action stream and captures a recording. The slot is separate
  // from the computer-use executor — both can run concurrently.

  async ensureRecordingExecutor(params: {
    agentRunId: string;
    baseUrl: string;
    bearerToken: string;
    /** Non-secret, same-origin metadata for native recording uploads. */
    uploadContext: ExecutorUploadContext;
  }): Promise<EnsureRecordingExecutorResult> {
    return invokeRpaCommand<EnsureRecordingExecutorResult>(
      'ensure_recording_executor',
      params
    );
  },

  /** Stop only if `agentRunId` still owns the recording executor slot. */
  async stopRecordingExecutorFor(agentRunId: string): Promise<boolean> {
    return invokeRpaCommand<boolean>('stop_recording_executor_for', { agentRunId });
  },

  // Owns no desktop-control lease (no presentation, redaction binding, or
  // panels), and its slot is independent of both other executors, so all
  // three can run concurrently. Same-device mode only; in target mode native
  // admission ensures the slot.

  async ensureFileExecutor(params: {
    agentRunId: string;
    baseUrl: string;
    bearerToken: string;
  }): Promise<EnsureFileExecutorResult> {
    return invokeRpaCommand<EnsureFileExecutorResult>('ensure_file_executor', params);
  },

  async attachExistingFileExecutor(params: {
    agentRunId: string;
    bearerToken: string;
  }): Promise<EnsureFileExecutorResult | null> {
    return invokeRpaCommand<EnsureFileExecutorResult | null>(
      'attach_existing_file_executor',
      params
    );
  },

  /** Stop only if `agentRunId` still owns the file executor slot. */
  async stopFileExecutorFor(agentRunId: string): Promise<boolean> {
    return invokeRpaCommand<boolean>('stop_file_executor_for', { agentRunId });
  },

  // -- Desktop-target control plane --
  //
  // Starts / stops the narrow native control loop that registers this desktop
  // as a computer-use target and handles `computer_use.target_assigned`
  // assignments over its own authenticated SSE connection. Auth, heartbeat
  // cadence, OS-lock suspend, capabilities, and target metadata all live in
  // Rust; the FE only supplies the validated base URL, then enables/stops the
  // runtime. Capabilities are sourced natively from the same `get_capabilities`
  // path this bridge exposes, so they can never drift from what this build can
  // dispatch.

  async desktopTargetStart(params: DesktopTargetStartParams): Promise<void> {
    return invokeRpaCommand<void>('desktop_target_start', {
      baseUrl: params.baseUrl,
    });
  },

  async desktopTargetStop(): Promise<void> {
    return invokeRpaCommand<void>('desktop_target_stop');
  },

  /** Public UUID only; reading does not create a native identity. */
  async desktopTargetInstanceId(): Promise<string | null> {
    return invokeRpaCommand<string | null>('desktop_target_instance_id');
  },

  /** Sign the final routing identity and canonical unsigned request body. */
  async desktopTargetSignProjectTurn(
    params: DesktopTargetSignProjectTurnParams
  ): Promise<DesktopTargetProjectTurnProof> {
    return invokeRpaCommand<DesktopTargetProjectTurnProof>(
      'desktop_target_sign_project_turn',
      {
        projectId: params.projectId,
        turnId: params.turnId,
        targetId: params.targetId ?? null,
        canonicalBody: params.canonicalBody,
      }
    );
  },
};

export type {
  AccessibilityAction,
  AccessibilityActionResult,
  AccessibilityElement,
  ActionResult,
  AxBatchResult,
  AxBatchStep,
  AxSelector,
  AxStepResult,
  BatchResult,
  CheckedState,
  ComputerUseReadiness,
  ControlKind,
  ControlState,
  CursorPositionResult,
  DesktopCapabilities,
  DesktopTargetProjectTurnProof,
  DesktopTargetSignProjectTurnParams,
  DesktopTargetStartParams,
  DropdownOption,
  ElementValue,
  EnsureRunExecutorResult,
  InspectFrom,
  InspectIncludeMode,
  InspectTargetInfo,
  InspectTargetMode,
  InspectUiRequest,
  InspectUiResult,
  InspectUiTarget,
  MouseButton,
  PerformActionRequest,
  Rect,
  RpaClientCapabilities,
  RpaExecutorStateSnapshot,
  RpaExecutorStatus,
  ScreenshotResult,
  ScrollDirectionName,
  SelectorScope,
  SetValueRequest,
  ToolAction,
} from './types';
